import React, { useCallback, useContext } from 'react';
import UserContext from '../../../contexts/user-context';
import { convertMultipleSeriesDataToExcel, Dates, DEFAULT_USE_LOGARITHMIC_SCALE, type ExcelCell } from 'venn-utils';
import { EMPTY } from '../../../studio-blocks/logic/exportUtils';
import {
  DownloadableContentBlock,
  formatExportableSubjectWithOptionalFee,
  useAppPrintMode,
  useBlockContext,
  WATERMARK_POSITION_TOP,
} from 'venn-components';
import { getCurrencySymbol } from '../../../studio-blocks/components/charts/chartsUtils';
import useExportUpdate from '../../../studio-blocks/logic/useExportUpdate';
import GrowthSimulationLineChart from '../../../studio-blocks/components/charts/GrowthSimulationLineChart';
import StyledEmptyState from '../../../studio-blocks/components/StyledEmptyState';
import { EmptyHeaderSpace } from '../../../content-block/DownloadableContentBlock';
import { useFilteredRequestsAndAnalyses } from '../../utils/hooks/use-filtered-requests';
import { useRecoilValue } from 'recoil';
import { blockExportMetadata, customizedBlock, isReportState } from 'venn-state';
import { useSelectedMetrics } from '../../../studio-blocks/logic/useSelectedMetrics';
import styled from 'styled-components';
import { useGrowthSimulationSeries } from '../hooks/use-growth-simulation-series';
import type { AnalysisProps } from '../../types';
import { useGetRequestSubjectFee } from '../../utils/hooks/use-advisory-fee';

export const GrowthSimulationContent = ({ id }: { id: string }) => {
  const exportMetaData = useRecoilValue(blockExportMetadata(id));
  const { requests, analyses } = useFilteredRequestsAndAnalyses(id);
  const isReport = useRecoilValue(isReportState);
  const { inPrintModeOrReportLab } = useAppPrintMode();

  return (
    <Chart
      id={id}
      requests={requests}
      analyses={analyses}
      exportMetaData={exportMetaData}
      inPrintMode={inPrintModeOrReportLab}
      isReport={isReport}
    />
  );
};

const Chart = ({ id, requests, analyses, exportMetaData, inPrintMode }: AnalysisProps) => {
  const block = useRecoilValue(customizedBlock(id));
  const metrics = useSelectedMetrics();
  const { blockRef: downloadableContentRef } = useBlockContext();

  const metric = metrics ? metrics[0] : undefined;
  const useLogarithmicScale = block.logarithmicScale ?? DEFAULT_USE_LOGARITHMIC_SCALE;
  const userContext = useContext(UserContext);
  const workspaceCurrency = userContext.profileSettings?.organization?.currency;

  const getRequestSubjectFee = useGetRequestSubjectFee();

  const [series, exportableSeries, subjectInfo] = useGrowthSimulationSeries(requests, analyses, metrics);

  const defaultHeader =
    block.relativeToBenchmark && metric?.relativeLabel
      ? metric.relativeLabel
      : !metric
        ? 'Builder Chart'
        : metric.label;

  const start = new Date();
  const end = new Date();
  end.setFullYear(start.getFullYear() + (requests[0]?.forecastWindow ?? 10));

  const excelDataFn = useCallback(() => {
    const headers: ExcelCell[] = [
      EMPTY,
      ...exportableSeries.flatMap(({ subject, series }) =>
        series.map((_serie, idx) =>
          idx === 0
            ? {
                value: subject.asOfDate
                  ? `${formatExportableSubjectWithOptionalFee(subject.subject, false, getRequestSubjectFee({ subject: subject.subject, isBenchmark: subject.subject.isBenchmark }))} (As of ${Dates.toDayMonthYear(subject.asOfDate)})`
                  : formatExportableSubjectWithOptionalFee(
                      subject.subject,
                      false,
                      getRequestSubjectFee({ subject: subject.subject, isBenchmark: subject.subject.isBenchmark }),
                    ),
                bold: true,
              }
            : EMPTY,
        ),
      ),
    ];
    const data = convertMultipleSeriesDataToExcel(
      exportableSeries
        .flatMap((subject) => subject.series)
        .map((serie) => ({
          name: serie.name,
          series: serie.data,
        })),
      false,
      getCurrencySymbol(workspaceCurrency),
    );
    return [headers, ...data];
  }, [exportableSeries, getRequestSubjectFee, workspaceCurrency]);

  useExportUpdate({ exportMetaData, excelDataFn, selectedRefId: id });

  const component =
    series.length > 0 ? (
      <ChartWrapper data-testid="qa-growth-simulation-chart">
        <GrowthSimulationLineChart
          start={start.getTime()}
          end={end.getTime()}
          series={series}
          frequency={requests?.[0]?.frequency ?? 'MONTHLY'}
          yAxisUnitFormat="ratio"
          inPrintMode={inPrintMode}
          useLogarithmicScale={useLogarithmicScale}
          currency={workspaceCurrency}
          subjectInfo={subjectInfo}
        />
      </ChartWrapper>
    ) : (
      <StyledEmptyState selectedRefId={id} header="Unable to run analysis" message="Chart is empty" />
    );

  if (inPrintMode) {
    return component;
  }

  return (
    <DownloadableContentBlock
      noBorder
      header={<EmptyHeaderSpace />}
      downloadable={{
        png: true,
        options: {
          fileName: block.header ? block.header : defaultHeader,
          watermark: { top: WATERMARK_POSITION_TOP, right: 20 },
        },
        tracking: {
          description: 'BUILDER',
          relativeToBenchmark: !!block.relativeToBenchmark,
          userUploaded: false,
          subjectType: undefined,
          subjectId: undefined,
        },
        disabled: series.length === 0,
        target: downloadableContentRef,
      }}
    >
      {component}
    </DownloadableContentBlock>
  );
};

const ChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  > div:first-child {
    flex: 1;
  }
`;
